/**
 * Admin Role Override Hook
 * =======================
 *
 * This hook enables alpha_admin users to override their role on any map via URL parameters.
 * This is useful for administrators and developers who need to test different permission levels
 * or access maps where they aren't invited ascollaborators.
 *
 * ## How to Use
 *
 * 1. Add `?role=:role` to any map URL where `:role` is one of:
 *    - `viewer` - Read-only access to maps and objectives
 *    - `commenter` - Can view and add comments
 *    - `editor` - Can edit objectives, statuses, results, and comments
 *
 * 2. The override only works if:
 *    - You have `accountType: 'alpha_admin'` in your user profile
 *    - The role parameter is one of the valid values above
 *
 * ## Examples
 *
 * ```
 * // Test as viewer
 * https://milestonemap.org/user123/maps/abc123?role=viewer
 *
 * // Test as commenter
 * https://milestonemap.org/user123/maps/abc123?role=commenter
 *
 * // Test as editor
 * https://milestonemap.org/user123/maps/abc123?role=editor
 * ```
 *
 * ## Visual Indicators
 *
 * When an admin override is active:
 * - TODO Add: Lightning bolt icon (⚡) and message to environment indicator
 *
 * ## Security Notes
 *
 * - TODO: Harden before Beta and GA
 *   - Log all actions for audit-ability
 *   - Trigger supervisor notifications
 *   - Commit admin list to code, authorize by uid per instance
 *   - Create a CloudFunction to cross-check current auth uid and authorized ids for overrides
 *
 * ## Architecture
 *
 * This approach keeps selectors pure (no side effects) while adding override logic at the hook level:
 * - `selectUserRoleForMap` remains a pure function that only depends on Redux state
 * - This hook reads URL parameters and applies override logic separately
 * - Components can choose between the pure selector or this enhanced hook
 *
 * ## Implementation
 *
 * Replace existing `selectUserRoleForMap` usage with `useRoleWithOverride`:
 *
 * ```typescript
 * // Before
 * const userRole = useSelector((state: RootState) =>
 *   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null
 * );
 *
 * // After
 * const userRole = useRoleWithOverride(currentMap?.uuid || '');
 * ```
 */

import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../store/store';
import { selectUserRoleForMap, selectUserProfile } from '../store/selectors';
import type { Role } from '@mm-shared/types/MilestoneMap1';

/**
 * Valid roles that can be used in URL override
 * Note: 'owner' is excluded as it should not be overrideable for security reasons
 */
const VALID_OVERRIDE_ROLES: Role[] = ['viewer', 'commenter', 'editor'];

/**
 * Hook that determines user role with admin override support via URL parameters
 *
 * Checks for ?role=:role URL parameter and allows alpha_admin users to override their role.
 * This is useful for admins and developers to test different permission levels.
 *
 * @param mapUuid - The UUID of the map to check role for
 * @returns The user's role (potentially overridden) or null if no access
 *
 * @example
 * // Normal usage - returns user's actual role
 * const role = useUserRoleWithOverride(mapUuid);
 *
 * // Admin override via URL: /map/abc123?role=editor
 * // If user is alpha_admin, returns 'editor' instead of actual role
 * const role = useRoleWithOverride(mapUuid); // returns 'editor'
 */
export function useRoleWithOverride(mapUuid: string): Role | null {
  const [searchParams] = useSearchParams();
  const userProfile = useSelector(selectUserProfile);

  // Get the user's actual role using the pure selector
  const actualRole = useSelector((state: RootState) =>
    mapUuid ? selectUserRoleForMap(state, mapUuid) : null,
  );

  // Get the role parameter from URL
  const roleParam = searchParams.get('role') as Role | null;

  // Determine if we should apply an override
  // TODO: Add logging for audit-ability
  const shouldOverride = (() => {
    if (!roleParam) return false;
    if (!VALID_OVERRIDE_ROLES.includes(roleParam)) {
      return false;
    }
    if (userProfile?.accountType !== 'alpha_admin') {
      return false;
    }
    return true;
  })();

  // Apply override if conditions are met
  if (shouldOverride && roleParam) {
    // TODO: Add logging for audit-ability
    console.warn('alpha_admin action');
    return roleParam;
  }

  // Return the actual role if no override
  return actualRole;
}
